import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { ContextStore } from '../store/contextStore';
import sharedContext from '../sharedContext';
import { usePageVisibility } from '../components/usePageVisibility';
import { fetchPreferences, updatePreferences, deletePreferenceKeys } from './preferencesApi';

const POLL_BASE_INTERVAL = 50000; // 50 seconds
const POLL_JITTER = 20000; // 0-20 seconds random jitter
const STORAGE_KEY = 'userPreferences';

export const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
  const { token, user } = useContext(ContextStore);
  const { enqueueSnackbar } = useSnackbar();
  const isPageVisible = usePageVisibility();

  // Initialize from localStorage
  const [preferences, setPreferencesState] = useState(() => {
    return sharedContext.get(STORAGE_KEY) || {};
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const pollTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // Calculate next poll interval with jitter
  const getNextPollInterval = useCallback(() => {
    return POLL_BASE_INTERVAL + Math.random() * POLL_JITTER;
  }, []);

  // Fetch preferences from API
  const refresh = useCallback(async () => {
    if (!token || !user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const prefs = await fetchPreferences(token);
      if (isMountedRef.current) {
        setPreferencesState(prefs);
        sharedContext.set(STORAGE_KEY, prefs, true);
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
      if (isMountedRef.current) {
        setError(err);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [token, user?.id]);

  // Setup polling
  useEffect(() => {
    isMountedRef.current = true;

    const schedulePoll = () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }

      if (isPageVisible && token && user?.id) {
        pollTimeoutRef.current = setTimeout(() => {
          refresh().then(() => {
            if (isMountedRef.current) {
              schedulePoll();
            }
          });
        }, getNextPollInterval());
      }
    };

    // Initial fetch
    if (token && user?.id) {
      refresh().then(() => {
        if (isMountedRef.current) {
          schedulePoll();
        }
      });
    }

    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [token, user?.id, isPageVisible, refresh, getNextPollInterval]);

  // Reschedule polling when visibility changes
  useEffect(() => {
    if (isPageVisible && token && user?.id) {
      // Resume polling when tab becomes visible
      refresh();
    } else {
      // Stop polling when tab is hidden
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    }
  }, [isPageVisible, token, user?.id, refresh]);

  // Read a single preference (sync, from local cache)
  const getPreference = useCallback((key, defaultValue = null) => {
    return preferences.hasOwnProperty(key) ? preferences[key] : defaultValue;
  }, [preferences]);

  // Update a single preference (optimistic)
  const setPreference = useCallback(async (key, value) => {
    if (!token || !user?.id) {
      enqueueSnackbar('Not authenticated', { variant: 'error' });
      return false;
    }

    const previousPrefs = { ...preferences };

    // Optimistic update
    const newPrefs = { ...preferences, [key]: value };
    setPreferencesState(newPrefs);
    sharedContext.set(STORAGE_KEY, newPrefs, true);

    try {
      await updatePreferences(token, user.id, { [key]: value });
      return true;
    } catch (err) {
      console.error('Failed to save preference:', err);
      // Rollback
      setPreferencesState(previousPrefs);
      sharedContext.set(STORAGE_KEY, previousPrefs, true);
      enqueueSnackbar('Failed to save preference', { variant: 'error' });
      return false;
    }
  }, [token, user?.id, preferences, enqueueSnackbar]);

  // Update multiple preferences (optimistic)
  const setPreferences = useCallback(async (updates) => {
    if (!token || !user?.id) {
      enqueueSnackbar('Not authenticated', { variant: 'error' });
      return false;
    }

    const previousPrefs = { ...preferences };

    // Optimistic update
    const newPrefs = { ...preferences, ...updates };
    setPreferencesState(newPrefs);
    sharedContext.set(STORAGE_KEY, newPrefs, true);

    try {
      await updatePreferences(token, user.id, updates);
      return true;
    } catch (err) {
      console.error('Failed to save preferences:', err);
      // Rollback
      setPreferencesState(previousPrefs);
      sharedContext.set(STORAGE_KEY, previousPrefs, true);
      enqueueSnackbar('Failed to save preferences', { variant: 'error' });
      return false;
    }
  }, [token, user?.id, preferences, enqueueSnackbar]);

  // Delete preference keys (optimistic)
  const deletePreferences = useCallback(async (keys) => {
    if (!token || !user?.id) {
      enqueueSnackbar('Not authenticated', { variant: 'error' });
      return false;
    }

    if (!Array.isArray(keys) || keys.length === 0) {
      return true;
    }

    const previousPrefs = { ...preferences };

    // Optimistic update - remove keys
    const newPrefs = { ...preferences };
    keys.forEach(key => delete newPrefs[key]);
    setPreferencesState(newPrefs);
    sharedContext.set(STORAGE_KEY, newPrefs, true);

    try {
      await deletePreferenceKeys(token, user.id, keys);
      return true;
    } catch (err) {
      console.error('Failed to delete preferences:', err);
      // Rollback
      setPreferencesState(previousPrefs);
      sharedContext.set(STORAGE_KEY, previousPrefs, true);
      enqueueSnackbar('Failed to delete preferences', { variant: 'error' });
      return false;
    }
  }, [token, user?.id, preferences, enqueueSnackbar]);

  const value = {
    preferences,
    isLoading,
    error,
    getPreference,
    setPreference,
    setPreferences,
    deletePreferences,
    refresh,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};
